import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import { Subject } from 'model/response';
import React, { useEffect, useState } from 'react';
import { Input, message, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import SubjectFormModal from './SubjectFormModal';
import { ModePage } from 'common/enum';
import processError from '../../../../utils/processError';
import { findAllSubject, toggleSubjectWebviewActive } from 'service/subjectService';
import { setSubjects } from '../../../../redux/slice/masterDataSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../hooks/useTableConfig';

export default function SubjectManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const [keyword, setKeyword] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [mode, setMode] = useState<ModePage>(ModePage.ADD);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const [data, setData] = useState<Subject[]>();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const columns: ColumnsType<Subject> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      width: '40%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Mã',
      dataIndex: 'code',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'môn học'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleSubjectWebviewActive(record.id)}
          reloadPageData={loadListSubjects}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('webViewOrder'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa môn học"
            description="Xác nhận xóa môn học. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteSubject(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteSubject = async (record: Subject) => {
    setLoading(true);
    try {
      await axiosInstance({
        url: `subjects/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công.');
      await loadListSubjects();
    } catch (err) {
      processError(err);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: Subject) => {
    setMode(ModePage.EDIT);
    setOpenModal(true);
    setSubjectId(record.id);
  };

  const loadListSubjects = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: 'subjects/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewSubject = async () => {
    setMode(ModePage.ADD);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBeforeSuccess = () => {
    setOpenModal(false);
    loadListSubjects().then();
    findAllSubject()
      .then((Subjects) => {
        dispatch(setSubjects(Subjects));
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    loadListSubjects().then();
  }, [currentPage, pageSize, sortBy, direction, keyword]);

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewSubject()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <SubjectFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        subjectId={subjectId}
      />
    </div>
  );
}
